import type { TileStatus, TileTint } from "@crew/ui/components/crew-side-panel";
import type { Activity, ThreadDetail, ThreadItem } from "./api";

/**
 * The thread rail (slice 1 spec §7.1): each thread is a tile on a tint of its own, and
 * the rail holds the few that matter most. A tile shows the thread's topic icon once
 * Clef has picked it (spike S-324), its letter until then; an iMessage thread's is
 * iMessage's own.
 */

/** Every tile tint `@crew/ui` draws, in the order a thread's hash picks from. */
export const TILE_TINTS: TileTint[] = ["peach", "sand", "sky", "lilac", "sage", "clay"];

/** How many threads the rail shows; the rest are in the finder behind "+N". */
const RAIL_SIZE = 8;

/** The tile's letter: the title's first letter or digit, upper-cased. */
export function tileLetter(title: string): string {
  const first = title.match(/[\p{L}\p{N}]/u);
  // Upper-casing can lengthen a letter ("ß" is "SS"): keep the first.
  return first ? Array.from(first[0].toLocaleUpperCase())[0]! : "·";
}

/** The tile's tint, the same for a thread every time: picked from its id. */
export function tileTint(id: string): TileTint {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return TILE_TINTS[hash % TILE_TINTS.length]!;
}

export function tileStatus(activity: Activity): TileStatus {
  // A handoff waiting has no ring: nothing runs (slice 6c §7.1).
  return activity === "running" ? "working" : activity === "handoff" ? null : activity;
}

/**
 * The rail's threads: running, then queued, then the newest, `size` of them. The open
 * thread is always on it, in the last place when it wouldn't be otherwise, even when
 * it isn't among the loaded ones (an older thread, loaded on its own).
 */
export function railThreads(
  threads: ThreadItem[],
  open?: ThreadItem,
  size = RAIL_SIZE,
): { shown: ThreadItem[]; more: number } {
  const rank = (t: ThreadItem) => (t.activity === "running" ? 0 : t.activity === "queued" ? 1 : 2);
  // `sort` is stable, so each rank stays newest first.
  let shown = [...threads].sort((a, b) => rank(a) - rank(b)).slice(0, size);
  const loaded = open ? threads.find((t) => t.id === open.id) : undefined;
  if (open && !shown.some((t) => t.id === open.id)) {
    shown = [...shown.slice(0, size - 1), loaded ?? open];
  }
  const listed = loaded ? threads.length : threads.length + (open ? 1 : 0);
  return { shown, more: listed - shown.length };
}

type AgentRow = { id: string; slug: string; name: string; glyph: string; tint: string };

/**
 * A thread's list row, worked out from its detail, for a thread the loaded list doesn't
 * have (an older one): what it's doing and who it's with, from its turns, the same way
 * the list decides (the running turn's agent, else the latest turn's).
 */
export function detailItem(detail: ThreadDetail, agents: AgentRow[]): ThreadItem {
  const { turns } = detail;
  const running = turns.find((t) => t.status === "running");
  const latest = turns.reduce<(typeof turns)[number] | undefined>(
    (last, t) => (!last || t.created_at > last.created_at ? t : last),
    undefined,
  );
  const activity = running ? "running" : turns.some((t) => t.status === "queued") ? "queued" : null;
  const agent = agents.find((a) => a.id === (running ?? latest)?.agent_id);
  return {
    ...detail.thread,
    started_by: null,
    preview: "",
    reply_count: 0,
    activity,
    agent: agent
      ? { id: agent.id, slug: agent.slug, name: agent.name, glyph: agent.glyph, tint: agent.tint }
      : null,
  };
}

/** The open thread's title: its detail's, else its row's, until either loads. */
export function threadTitle(detail: ThreadDetail | undefined, row: ThreadItem | undefined) {
  return (detail?.thread ?? row)?.title ?? "Thread";
}
